<template>
  <a
    v-if="href"
    :href="href"
    target="_blank"
    rel="noopener noreferrer"
    :class="variant === 'inline' ? 'whatsappInline' : 'whatsappFloat'"
    :aria-label="variant === 'inline' ? label : 'Escríbenos por WhatsApp'"
    @click="trackClick"
  >
    <img src="../assets/WhatsApp.webp" alt="" aria-hidden="true">
    <span v-if="variant === 'inline'">{{ label }}</span>
  </a>
</template>

<script>
import { inject, computed, ref } from 'vue';
import { useRoute } from 'vue-router';

// Mensaje precargado por contexto: permite identificar de qué página vino el
// contacto sin depender del formulario (plan 22_08_2026, R7).
const MESSAGES_BY_PATH = {
  '/': 'Hola, vengo de la página de inicio y quiero más información.',
  '/print3d': 'Hola, quiero cotizar una impresión 3D.',
  '/services': 'Hola, quiero información sobre sus servicios.',
  '/catalog': 'Hola, vi el catálogo y quiero más información.',
  '/contact': 'Hola, quiero hablar sobre un proyecto.',
  '/us': 'Hola, quiero conocer más sobre Selk.',
};

const DEFAULT_MESSAGE = 'Hola, quiero más información.';

export default {
  name: 'WhatsAppButton',
  props: {
    // 'floating': burbuja fija de toda la web. 'inline': CTA dentro del contenido.
    variant: {
      type: String,
      default: 'floating',
      validator: (v) => ['floating', 'inline'].includes(v),
    },
    // Mensaje precargado; si se omite, se deduce de la ruta actual.
    message: { type: String, default: '' },
    label: { type: String, default: 'Escríbenos por WhatsApp' },
  },
  setup(props) {
    // Default por si el componente se monta fuera del árbol que provee `company`.
    const company = inject('company', ref(null));
    const route = useRoute();

    const text = computed(
      () => props.message || MESSAGES_BY_PATH[route.path] || DEFAULT_MESSAGE
    );

    const href = computed(() => {
      const phone = company.value?.phone;
      if (!phone) return '';
      return `https://wa.me/57${phone}?text=${encodeURIComponent(text.value)}`;
    });

    // Conversión medible: sin esto, pasar el contacto a WhatsApp haría perder
    // la trazabilidad que hoy da el formulario (§6 del plan).
    const trackClick = () => {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'contact_whatsapp', {
          source: route.path,
          variant: props.variant,
        });
      }
    };

    return { href, trackClick };
  },
};
</script>

<style scoped>
/* ---------- Burbuja flotante ---------- */
.whatsappFloat {
  position: fixed;
  right: 2%;
  bottom: 5%;
  background-color: #25D366;
  border-radius: 50%;
  width: 65px;
  height: 65px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 3;
  transition: transform 0.3s;
}

.whatsappFloat:hover {
  transform: scale(1.1);
}

.whatsappFloat img {
  width: 80%;
  height: auto;
}

/* ---------- CTA en línea ---------- */
.whatsappInline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  background-color: #25D366;
  color: var(--color-white);
  font-family: var(--font-body);
  font-size: 18px;
  text-decoration: none;
  padding: 0.9rem 1.4rem;
  border-radius: 14px;
  box-shadow: 0px 10px 25px -8px rgba(37, 211, 102, 0.55);
  transition: background-color 0.2s ease, transform 0.25s ease, box-shadow 0.25s ease;
}

.whatsappInline:hover {
  background-color: #1EBE5B;
  transform: translateY(-3px);
  box-shadow: 0px 14px 30px -8px rgba(37, 211, 102, 0.65);
}

.whatsappInline img {
  width: 24px;
  height: 24px;
}

@media (max-width: 550px) {
  .whatsappInline {
    width: 100%;
    box-sizing: border-box;
  }
}
</style>
